// Shared display helpers for the Chargebacks list + detail pages (PROD-7808).

import { hasAccess } from '../../../../util_functions';

// Currencies whose minor unit is not 1/100 (mirrors the goserver fmtAmount, plan R15).
export const CURRENCY_DECIMALS = {
  jpy: 0,
  krw: 0,
  vnd: 0,
  clp: 0,
  xof: 0,
  xaf: 0,
  kwd: 3,
  bhd: 3,
  omr: 3,
  tnd: 3
};

// fmt_amount renders raw Stripe minor units in the currency's real precision.
export const fmt_amount = (minor, code) => {
  const d = CURRENCY_DECIMALS[(code || '').toLowerCase()] ?? 2;
  const value = d === 0 ? minor || 0 : (minor || 0) / 10 ** d;
  return `${d === 0 ? value : value.toFixed(d)} ${(code || '').toUpperCase()}`;
};

// lost_status_label: internal_status → the human phrase shown on lost-chargeback banners.
export const lost_status_label = (internal_status) => {
  const s = String(internal_status || '').toUpperCase();
  if (s === 'RESOLVED') return 'resolved';
  if (s === 'NO_ACTION') return 'reviewed, no action';
  return 'action pending';
};

// role_can_see_chargebacks: H-M6 rev2 — mirror the SERVER's signal. goserver by_student
// (dispute/api_cms.go authorizeRead) authorizes by ROLE, never access_pages; gating on
// '/chargebacks' page-membership silently hid the banner from authorized ops/care users.
const CHARGEBACK_ROLE_TOKENS = ['SUPER_USER', 'CARE', 'ENGINEERING', 'TEACHER_OPS'];
export const role_can_see_chargebacks = (access_type) =>
  hasAccess(access_type, ...CHARGEBACK_ROLE_TOKENS);

// has_super_user_access mirrors the goserver superUserGate — an exact-string match would deny a
// legitimate combined role like "SUPER_USER,CARE" the action panel while the server allowed the
// action itself.
export const has_super_user_access = (access_type) => hasAccess(access_type, 'SUPER_USER');

// select_lost_banner picks the most recent lost dispute (+ how many more) for the
// student-profile banner; null = no banner. Pure so it's unit-testable (PROD-7808 T6).
export const select_lost_banner = (rows) => {
  const lost = (Array.isArray(rows) ? rows : []).filter(
    (r) => r && String(r.status || '').toLowerCase() === 'lost'
  );
  if (lost.length === 0) return null;
  const sorted = [...lost].sort(
    (a, b) => new Date(b.stripe_created_date || 0) - new Date(a.stripe_created_date || 0)
  );
  return {
    dispute: sorted[0],
    extra_count: lost.length - 1,
    status_label: lost_status_label(sorted[0].internal_status)
  };
};

// status_label / internal_status_label: raw Stripe + internal enum values are jargon to a care
// agent (needs_response, NO_ACTION, warning_closed…). Map them to plain English (PROD-7808 UX #1).
export const STATUS_LABELS = {
  needs_response: 'Needs response',
  warning_needs_response: 'Needs response (early-fraud warning)',
  under_review: 'Under review by Stripe',
  warning_under_review: 'Under review (early-fraud warning)',
  won: 'Won',
  lost: 'Lost',
  warning_closed: 'Closed (early-fraud warning)',
  charge_refunded: 'Refunded',
  // EFW (early-fraud-warning) pseudo-statuses — the list mixes EFW rows in with disputes.
  actionable: 'Action needed',
  not_actionable: 'No action needed'
};
export const status_label = (status) =>
  STATUS_LABELS[String(status || '').toLowerCase()] || status || '--';

export const INTERNAL_STATUS_LABELS = {
  NEW: 'New',
  REVIEWING: 'Reviewing',
  EVIDENCE_SUBMITTED: 'Evidence submitted',
  ACCEPTED: 'Accepted (conceded)',
  WONT_CONTEST: "Won't contest",
  CLOSED: 'Closed',
  RESOLVED: 'Resolved',
  NO_ACTION: 'Reviewed — no action (retained)'
};
export const internal_status_label = (s) =>
  INTERNAL_STATUS_LABELS[String(s || '').toUpperCase()] || s || '--';

// reason_label: Stripe reason codes are jargon — give the care agent a plain-English gloss so they
// know what the customer actually claimed (PROD-7808 UX clarity). Unknown codes pass through.
export const REASON_LABELS = {
  fraudulent: 'Fraudulent — cardholder says they did not authorize the charge',
  subscription_canceled: 'Subscription cancelled — says they had already cancelled',
  product_not_received: 'Product not received',
  product_unacceptable: 'Product unacceptable',
  duplicate: 'Duplicate — says they were charged twice',
  credit_not_processed: 'Credit not processed — expected a refund they did not get',
  unrecognized: 'Unrecognized — does not recognise the charge',
  general: 'General dispute (no specific reason given)',
  incorrect_account_details: 'Incorrect account details',
  insufficient_funds: 'Insufficient funds',
  bank_cannot_process: 'Bank could not process',
  debit_not_authorized: 'Debit not authorized',
  customer_initiated: 'Customer-initiated dispute',
  // EFW (early-fraud-warning) fraud types — Stripe's Radar early-warning taxonomy.
  unauthorized_use_of_card: 'Unauthorized use of card',
  made_with_stolen_card: 'Made with a stolen card',
  made_with_lost_card: 'Made with a lost card',
  made_with_counterfeit_card: 'Made with a counterfeit card',
  card_never_received: 'Card never received',
  fraudulent_card_application: 'Fraudulent card application',
  misc: 'Other suspected fraud'
};
export const reason_label = (r) => REASON_LABELS[String(r || '').toLowerCase()] || r || '--';

// next_step: the single "what do I do now?" line at the top of the detail page, driven by the
// dispute's state (PROD-7808 UX #2). Terminal internal_status wins over the Stripe status (a lost
// dispute we already resolved is "done", not "revoke"). Returns { text, tone }; text '' = no banner.
export const next_step = (dispute, now = new Date()) => {
  const d = dispute || {};
  const s = String(d.status || '').toLowerCase();
  const i = String(d.internal_status || '').toUpperCase();
  if (i === 'RESOLVED') {
    return { text: 'Resolved — the remedy is in effect. Nothing more to do.', tone: 'success' };
  }
  if (i === 'NO_ACTION') {
    return {
      text: 'Reviewed and retained — no account changes. Nothing more to do.',
      tone: 'success'
    };
  }
  if (i === 'EVIDENCE_SUBMITTED') {
    return { text: 'Evidence submitted — awaiting Stripe’s decision.', tone: 'info' };
  }
  if (s === 'won' || s === 'warning_closed') {
    return { text: 'We won this dispute — no action needed.', tone: 'success' };
  }
  if (s === 'charge_refunded') {
    return { text: 'The charge was refunded — no action needed.', tone: 'info' };
  }
  if (s === 'lost') {
    return {
      text: 'Lost — revoke the membership + sessions, or retain the customer, below.',
      tone: 'error'
    };
  }
  if (s === 'needs_response' || s === 'warning_needs_response') {
    const due = d.evidence_due_by ? new Date(d.evidence_due_by) : null;
    const passed = due && !Number.isNaN(due.getTime()) && due.getTime() <= now.getTime();
    if (passed) {
      return { text: 'Response deadline passed — awaiting Stripe’s decision.', tone: 'info' };
    }
    return {
      text: 'Needs response — submit evidence to contest, or accept, before the deadline.',
      tone: 'warning'
    };
  }
  if (s === 'under_review' || s === 'warning_under_review') {
    return { text: 'Under review by Stripe — awaiting their decision.', tone: 'info' };
  }
  return { text: '', tone: 'info' };
};

// status_badge maps a dispute/EFW status to theme-token badge colors (never hardcode colors).
export const status_badge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'lost' || s === 'charge_refunded') {
    return { background: 'var(--state-error-bg)', color: 'var(--state-error-text)' };
  }
  if (s === 'won') {
    return { background: 'var(--state-success-bg)', color: 'var(--state-success-text)' };
  }
  if (s.includes('needs_response') || s === 'actionable') {
    return { background: 'var(--state-warning-bg)', color: 'var(--state-warning-text)' };
  }
  return { background: 'var(--state-info-bg)', color: 'var(--state-info-text)' };
};
